import { clsx } from 'clsx'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { Time } from '../common/components/Time.js'
import { dayjs } from '../common/lib/dayjs.js'
import { T } from '../i18n/components/T.js'

import { getLogIcon } from './AuditLogRow.js'

const sampleLogs = [
  {
    category: ['network'],
    key: 'network-post',
    noun: 'NETWORK',
    sentence: (
      <T
        id="AuditLog.UpdatedNetwork"
        defaultMessage="{actor_link} updated {COMMUNITY} settings"
        values={{ actor_link: <Link>Jane</Link> }}
      />
    ),
    time: dayjs().add(-2, 'minutes'),
  },
  {
    category: ['network', 'member'],
    key: 'changed-role',
    noun: 'MEMBER',
    sentence: (
      <T
        id="AuditLog.ChangedRole"
        defaultMessage="{actor_link} changed {member_link}’s role to moderator"
        values={{
          actor_link: <Link>Mary</Link>,
          member_link: <Link>John</Link>,
        }}
      />
    ),
    time: dayjs().add(-5, 'minutes'),
  },
  {
    category: ['post', 'member', 'space', 'network'],
    key: 'followed-post',
    noun: 'POST',
    sentence: (
      <T
        id="AuditLog.FollowedPost"
        defaultMessage="{actor_link} followed {post_link}"
        values={{
          actor_link: <Link>Mary</Link>,
          post_link: <Link>This looks amazing</Link>,
        }}
      />
    ),
    time: dayjs().add(-7, 'minutes'),
  },
  {
    category: ['post', 'member', 'space', 'network'],
    key: 'reacted-post',
    noun: 'REACTION',
    sentence: (
      <T
        id="AuditLog.ReactedPost"
        defaultMessage="{actor_link} reacted to {post_link}"
        values={{
          actor_link: <Link>Mary</Link>,
          post_link: <Link>This looks amazing</Link>,
        }}
      />
    ),
    time: dayjs().add(-11, 'minutes'),
  },
  {
    category: ['network', 'space'],
    key: 'invited-space',
    noun: 'SPACE',
    sentence: (
      <T
        id="AuditLog.InvitedToSpace"
        defaultMessage="{actor_link} added {member_link} to {space_link}"
        values={{
          actor_link: <Link>Jane</Link>,
          member_link: <Link>John</Link>,
          space_link: <Link>Getting started</Link>,
        }}
      />
    ),
    time: dayjs().add(-14, 'minutes'),
  },
  {
    category: ['space', 'network'],
    key: 'created-space',
    noun: 'SPACE',
    sentence: (
      <T
        id="AuditLog.CreatedSpace"
        defaultMessage="{actor_link} created {space_link}"
        values={{
          actor_link: <Link>Jane</Link>,
          space_link: <Link>Getting started</Link>,
        }}
      />
    ),
    time: dayjs().add(-16, 'minutes'),
  },
  {
    category: ['member', 'network'],
    key: 'updated-sso',
    noun: 'NETWORK',
    sentence: (
      <T
        id="AuditLog.UpdatedSso"
        defaultMessage="{actor_link} updated {COMMUNITY} SSO settings"
        values={{
          actor_link: <Link>Mary</Link>,
        }}
      />
    ),
    time: dayjs().add(-23, 'minutes'),
  },
  {
    category: ['network'],
    key: 'verified-member',
    noun: 'MEMBER',
    sentence: (
      <T
        id="AuditLog.VerifiedAccount"
        defaultMessage="{actor_link} verified their account"
        values={{
          actor_link: <Link>Jack</Link>,
        }}
      />
    ),
    time: dayjs().add(-28, 'minutes'),
  },
  {
    category: ['network', 'member'],
    key: 'change-password',
    noun: 'MEMBER',
    sentence: (
      <T
        id="AuditLog.ChangedPassword"
        defaultMessage="{actor_link} changed their password"
        values={{
          actor_link: <Link>Mary</Link>,
        }}
      />
    ),
    time: dayjs().add(-35, 'minutes'),
  },
  {
    category: ['post', 'space', 'member', 'network'],
    key: 'approved-post',
    noun: 'MODERATION',
    sentence: (
      <T
        id="AuditLog.ApprovedPost"
        defaultMessage="{actor_link} approved {post_link}"
        values={{
          actor_link: <Link>Mary</Link>,
          post_link: <Link>This looks amazing</Link>,
        }}
      />
    ),
    time: dayjs().add(-55, 'minutes'),
  },
  {
    category: ['post', 'space', 'network'],
    key: 'flagged-post',
    noun: 'MODERATION',
    sentence: (
      <T
        id="AuditLog.GotFlagged"
        defaultMessage="{actor_link}’s {post_link} was flagged for moderation"
        values={{
          actor_link: <Link>Jeff</Link>,
          post_link: <Link>This looks amazing</Link>,
        }}
      />
    ),
    time: dayjs().add(-65, 'minutes'),
  },
  {
    category: ['post', 'space', 'network'],
    key: 'created-post',
    noun: 'POST',
    sentence: (
      <T
        id="AuditLog.Posted"
        defaultMessage="{actor_link} posted {post_link}"
        values={{
          actor_link: <Link>Jeff</Link>,
          post_link: <Link>This looks amazing</Link>,
        }}
      />
    ),
    time: dayjs().add(-65, 'minutes'),
  },
]

export const getSampleLogs = items => (
  <div className="relative opacity-50">
    <List spacing="md">
      {items.map(({ key, noun, sentence, time }) => {
        const { icon, color: iconColor } = getLogIcon(noun)
        return (
          <List.Item key={key} className="flex items-center space-s-3">
            <div className={clsx('rounded-full p-2', iconColor)}>
              <SvgIcon name={icon} size="lg" />
            </div>
            <div>
              <div>{sentence}</div>
              <Time dateTime={time} className="text-sm">
                {time.fromNow()}
              </Time>
            </div>
          </List.Item>
        )
      })}
    </List>
    <div className="absolute -z-10 w-[2px] ms-[1px] top-8 start-4 bottom-8 bg-surface-pressed" />
    <div className="absolute inset-0"></div>
  </div>
)

export const SampleAuditLogs = ({ type }) => {
  return getSampleLogs(
    sampleLogs.filter(log => log?.category?.indexOf(type) !== -1),
  )
}
